import React, { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../supabaseClient'
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, Cell, AreaChart, Area
} from "recharts"
import { authHeaders } from '../../utils/authFetch'

import { useBranchLoyaltyAIHistory } from '../../hooks/useBranchLoyaltyAIHistory'

if (typeof document !== 'undefined' && !document.getElementById('shimmer-keyframes')) {
    const style = document.createElement('style')
    style.id = 'shimmer-keyframes'
    style.innerHTML = `
        @keyframes shimmer {
            0% { background-position: -400px 0; }
            100% { background-position: 400px 0; }
        }
    `
    document.head.appendChild(style)
}

// ─── Design tokens (matches LoyaltyAnalytics.jsx) ─────────────────────────
const C = {
    dark: '#F9FAFB',
    card: '#FFFFFF',
    border: '#E5E7EB',
    muted: '#9CA3AF',
    text: '#111827',
    textSoft: '#6B7280',
    amber: '#D97706',
    gold: '#B45309',
    teal: '#0D9488',
    coral: '#DC2626',
    blue: '#2563EB',
    green: '#16A34A',
    purple: '#7C3AED',
    nude: '#A0826D',
}

const presetStart = (range) => {
    if (range === "all") return null
    const days = { "7d": 7, "30d": 30, "90d": 90, "1y": 365 }[range] ?? 30
    return new Date(Date.now() - days * 86400000).toISOString()
}

// ─── Shared UI primitives ──────────────────────────────────────────────
const Card = ({ children, className = '' }) => (
    <div className={`rounded-2xl border p-5 ${className}`}
        style={{ background: C.card, borderColor: C.border }}>
        {children}
    </div>
)

const SectionTitle = ({ title, subtitle }) => (
    <div className="mb-5">
        <h2 className="m-0 text-[17px] font-bold tracking-tight" style={{ color: C.text }}>{title}</h2>
        {subtitle && <p className="m-0 text-xs mt-0.5" style={{ color: C.textSoft }}>{subtitle}</p>}
    </div>
)

const KPI = ({ label, value, color = C.amber, loading = false }) => (
    <div className="rounded-xl p-3.5 min-w-0 border" style={{ borderColor: C.border }}>
        <p className="m-0 mb-1 text-[11px] uppercase tracking-widest" style={{ color: C.muted }}>{label}</p>
        {loading ? (
            <SkeletonBlock className="h-6 w-16 mb-0.5" />
        ) : (
            <p className="mt-0 mb-0.5 text-[22px] font-bold" style={{ color }}>{value}</p>
        )}
    </div>
)

const Spinner = ({ height = 160 }) => (
    <div className="flex items-center justify-center gap-2" style={{ height }}>
        <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
            <path fill={C.amber} d="M4 12a8 8 0 018-8v8z" />
        </svg>
        <span className="text-xs" style={{ color: C.muted }}>Loading…</span>
    </div>
)

const SkeletonBlock = ({ className = '', style = {} }) => (
    <div
        className={`rounded ${className}`}
        style={{
            backgroundImage: `linear-gradient(90deg, ${C.border} 0px, ${C.dark} 40px, ${C.border} 80px)`,
            backgroundSize: '400px 100%',
            animation: 'shimmer 1.4s ease-in-out infinite',
            ...style,
        }}
    />
)

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null
    return (
        <div className="rounded-lg px-3 py-2 text-xs border shadow-sm"
            style={{ background: '#fff', borderColor: C.border }}>
            <p className="m-0 mb-1.5 font-semibold" style={{ color: C.text }}>{label}</p>
            {payload.map((p, i) => (
                <p key={i} className="m-0 my-0.5" style={{ color: p.color || C.textSoft }}>
                    {p.name}: <strong>{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}</strong>
                </p>
            ))}
        </div>
    )
}

const StatusBadge = ({ status }) => {
    const map = {
        completed: { bg: C.green + '22', color: C.green, label: 'Used' },
        pending: { bg: C.amber + '22', color: C.amber, label: 'Claimed' },
        cancelled: { bg: C.coral + '22', color: C.coral, label: 'Cancelled' },
        expired: { bg: C.muted + '22', color: C.muted, label: 'Expired' },
    }
    const cfg = map[status] ?? { bg: C.muted + '22', color: C.muted, label: status }
    return (
        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
            style={{ background: cfg.bg, color: cfg.color }}>
            {cfg.label}
        </span>
    )
}

const RangeSelector = ({ range, setRange }) => {
    const options = [
        { label: "All Time", value: "all" },
        { label: "7 days", value: "7d" },
        { label: "30 days", value: "30d" },
        { label: "90 days", value: "90d" },
        { label: "1 year", value: "1y" },
    ]
    return (
        <div className="flex gap-1.5">
            {options.map((o) => (
                <button key={o.value} onClick={() => setRange(o.value)}
                    className="px-3 py-1 rounded-full text-[11px] border transition-colors"
                    style={{
                        background: range === o.value ? C.amber : C.card,
                        borderColor: range === o.value ? C.amber : C.border,
                        color: range === o.value ? "#fff" : C.textSoft,
                        cursor: "pointer",
                    }}>
                    {o.label}
                </button>
            ))}
        </div>
    )
}

function Pagination({ page, totalItems, pageSize, onPageChange }) {
    const totalPages = Math.ceil(totalItems / pageSize)
    if (totalPages <= 1) return null
    return (
        <div className="flex items-center justify-between px-1 py-2">
            <span style={{ fontSize: 11, color: C.muted }}>
                {Math.min((page - 1) * pageSize + 1, totalItems)}–{Math.min(page * pageSize, totalItems)} of {totalItems} items
            </span>
            <div className="flex items-center gap-1">
                <button onClick={() => onPageChange(Math.max(1, page - 1))} disabled={page === 1}
                    style={{
                        padding: "3px 10px", borderRadius: 8, border: `1px solid ${C.border}`,
                        background: C.card, color: page === 1 ? C.border : C.textSoft,
                        fontSize: 11, cursor: page === 1 ? "not-allowed" : "pointer",
                    }}>← Prev</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                    .reduce((acc, n, idx, arr) => {
                        if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
                        acc.push(n)
                        return acc
                    }, [])
                    .map((n, idx) => n === "…" ? (
                        <span key={`e-${idx}`} style={{ fontSize: 11, color: C.muted, padding: "0 2px" }}>…</span>
                    ) : (
                        <button key={n} onClick={() => onPageChange(n)}
                            style={{
                                width: 28, height: 28, borderRadius: 8,
                                border: `1px solid ${n === page ? C.nude : C.border}`,
                                background: n === page ? C.nude : C.card,
                                color: n === page ? "#fff" : C.textSoft,
                                fontSize: 11, fontWeight: n === page ? 600 : 400, cursor: "pointer",
                            }}>{n}</button>
                    ))}
                <button onClick={() => onPageChange(Math.min(totalPages, page + 1))} disabled={page === totalPages}
                    style={{
                        padding: "3px 10px", borderRadius: 8, border: `1px solid ${C.border}`,
                        background: C.card, color: page === totalPages ? C.border : C.textSoft,
                        fontSize: 11, cursor: page === totalPages ? "not-allowed" : "pointer",
                    }}>Next →</button>
            </div>
        </div>
    )
}

export function useCurrentBranch() {
    const [branchId, setBranchId] = useState(null)
    const [branchName, setBranchName] = useState(null)
    const [role, setRole] = useState(null)
    const [canViewAnalytics, setCanViewAnalytics] = useState(false)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const load = async () => {
            const staffId = localStorage.getItem('staffId') || sessionStorage.getItem('staffId')
            if (!staffId) { setLoading(false); return }

            const { data, error } = await supabase
                .rpc('get_staff_branch', { p_staff_id: staffId })
                .single()

            if (!error && data) {
                setBranchId(data.branch_id)
                setBranchName(data.branch_name ?? null)
                setRole(data.role ?? null)
                setCanViewAnalytics(data.role === 'Admin' && !!data.permissions?.canViewAnalytics)
            } else if (error) {
                console.error('useCurrentBranch error:', error)
            }
            setLoading(false)
        }
        load()
    }, [])

    return { branchId, branchName, role, canViewAnalytics, loading }
}

function useBranchLoyaltyData(branchId) {
    const [tiers, setTiers] = useState(null)
    const [accounts, setAccounts] = useState(null)
    const [redemptions, setRedemptions] = useState(null)

    useEffect(() => {
        if (!branchId) return
        setTiers(null); setAccounts(null); setRedemptions(null)

        const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
        fetch(`${BASE_URL}/api/loyalty-analytics/branch-data?branchId=${branchId}`, { headers: authHeaders() })
            .then(res => res.json())
            .then(data => {
                setTiers(data.tiers ?? [])
                setAccounts(data.accounts ?? [])
                setRedemptions(data.redemptions ?? [])
            })
            .catch(err => console.error('Error fetching branch loyalty analytics data:', err))
    }, [branchId])

    return { tiers, accounts, redemptions }
}

// ─── Tier Distribution (branch) ───────────────────────────────────────
const BranchTierPanel = ({ tiers, accounts, loading }) => {
    const tierData = useMemo(() => {
        if (!tiers || !accounts) return []
        const countMap = {}
        for (const acc of accounts) {
            if (!acc.current_tier_id) continue
            countMap[acc.current_tier_id] = (countMap[acc.current_tier_id] || 0) + 1
        }
        const untiered = accounts.filter(a => !a.current_tier_id).length
        const rows = tiers.map(t => ({
            name: t.tier_name, count: countMap[t.tier_id] || 0, color: t.tier_color,
        }))
        if (untiered > 0) rows.push({ name: 'No Tier', count: untiered, color: C.muted })
        return rows
    }, [tiers, accounts])

    const total = tierData.reduce((s, t) => s + t.count, 0)
    const active = (accounts ?? []).filter(a => a.is_active).length

    return (
        <Card>
            <SectionTitle title="Branch Member Tier Distribution" subtitle="Members who have redeemed a reward at this branch" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-5">
                <KPI label="Members at this branch" value={total.toLocaleString()} color={C.blue} loading={loading} />
                <KPI label="Active members" value={active.toLocaleString()} color={C.green} loading={loading} />
            </div>
            <ResponsiveContainer debounce={200} width="100%" height={200}>
                <BarChart data={tierData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                    <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: C.muted, fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: C.muted, fontSize: 9 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Members" radius={[4, 4, 0, 0]}>
                        {tierData.map((t, i) => <Cell key={i} fill={t.color} />)}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </Card>
    )
}

// ─── Top Rewards + Monthly Trend (branch) ─────────────────────────────
const BranchRewardsChart = ({ redemptions, loading }) => {
    const { chartData, monthly } = useMemo(() => {
        if (!redemptions) return { chartData: [], monthly: [] }
        const rewardMap = {}, monthMap = {}
        const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
        for (const r of redemptions) {
            const name = r.rewards?.reward_name ?? 'Unknown'
            if (!rewardMap[name]) rewardMap[name] = { name, completed: 0, pending: 0, cancelled: 0 }
            if (r.redemption_status === 'completed') rewardMap[name].completed++
            else if (r.redemption_status === 'pending') rewardMap[name].pending++
            else rewardMap[name].cancelled++

            const month = MONTHS[new Date(r.redeemed_at).getMonth()]
            if (!monthMap[month]) monthMap[month] = { month, redemptions: 0 }
            monthMap[month].redemptions++
        }
        const chartData = Object.values(rewardMap)
            .sort((a, b) => (b.completed + b.pending + b.cancelled) - (a.completed + a.pending + a.cancelled))
            .slice(0, 8)
        const monthly = Object.values(monthMap).sort((a, b) => MONTHS.indexOf(a.month) - MONTHS.indexOf(b.month))
        return { chartData, monthly }
    }, [redemptions])

    if (loading) return <Card><SectionTitle title="Branch Redemption Analytics" /><Spinner height={220} /></Card>

    return (
        <Card>
            <SectionTitle title="Branch Redemption Analytics" subtitle="Most redeemed rewards and monthly trend for this branch" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div>
                    <p className="text-[11px] uppercase tracking-wider m-0 mb-2" style={{ color: C.muted }}>Top Redeemed Rewards</p>
                    {chartData.length === 0
                        ? <p className="text-xs" style={{ color: C.muted }}>No redemption data yet.</p>
                        : (
                            <ResponsiveContainer debounce={200} width="100%" height={200}>
                                <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 10, left: 10, bottom: 0 }}>
                                    <CartesianGrid stroke={C.border} strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" tick={{ fill: C.muted, fontSize: 9 }} axisLine={false} tickLine={false} />
                                    <YAxis type="category" dataKey="name" width={110} tick={{ fill: C.textSoft, fontSize: 10 }} axisLine={false} tickLine={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Bar dataKey="completed" name="Completed" fill={C.green} stackId="a" />
                                    <Bar dataKey="pending" name="Pending" fill={C.amber} stackId="a" />
                                    <Bar dataKey="cancelled" name="Cancelled" fill={C.coral} stackId="a" radius={[0, 4, 4, 0]} />
                                    <Legend wrapperStyle={{ fontSize: 10, color: C.textSoft }} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                </div>
                <div>
                    <p className="text-[11px] uppercase tracking-wider m-0 mb-2" style={{ color: C.muted }}>Monthly Redemption Volume</p>
                    <ResponsiveContainer debounce={200} width="100%" height={200}>
                        <AreaChart data={monthly} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                            <defs>
                                <linearGradient id="branchLoyaltyGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={C.amber} stopOpacity={0.4} />
                                    <stop offset="95%" stopColor={C.amber} stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="month" tick={{ fill: C.muted, fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: C.muted, fontSize: 9 }} axisLine={false} tickLine={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Area type="monotone" dataKey="redemptions" name="Redemptions" stroke={C.amber} fill="url(#branchLoyaltyGrad)" strokeWidth={2} dot={false} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </Card>
    )
}

// ─── Redemption Table (branch) ────────────────────────────────────────
const PAGE_SIZE = 10

const BranchRedemptionTable = ({ redemptions, accounts, loading }) => {
    const [range, setRange] = useState("all")
    const [statusFilter, setStatusFilter] = useState(null)
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)

    const startISO = useMemo(() => presetStart(range), [range])

    const dateFiltered = useMemo(() => {
        if (!redemptions) return []
        if (!startISO) return redemptions
        return redemptions.filter(r => new Date(r.redeemed_at) >= new Date(startISO))
    }, [redemptions, startISO])

    const membershipMap = useMemo(() => {
        const map = {}
        for (const acc of accounts ?? []) map[acc.loyalty_id] = acc.membership_number
        return map
    }, [accounts])

    const filtered = useMemo(() => {
        return dateFiltered
            .filter(r => !statusFilter || r.redemption_status === statusFilter)
            .filter(r => {
                if (!search) return true
                const q = search.toLowerCase()
                const memberNo = membershipMap[r.loyalty_id] ?? ''
                const rewardName = r.rewards?.reward_name ?? ''
                return memberNo.toLowerCase().includes(q) || rewardName.toLowerCase().includes(q)
            })
    }, [dateFiltered, statusFilter, search, membershipMap])

    const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    const statuses = ['completed', 'pending', 'cancelled', 'expired']
    const statusLabels = { completed: 'Used', pending: 'Claimed', cancelled: 'Cancelled', expired: 'Expired' }
    const statusColors = { completed: C.green, pending: C.amber, cancelled: C.coral, expired: C.muted }

    const summary = useMemo(() => dateFiltered.reduce((acc, r) => {
        acc[r.redemption_status] = (acc[r.redemption_status] || 0) + 1
        return acc
    }, {}), [dateFiltered])

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

    return (
        <Card>
            <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
                <SectionTitle title="Branch Redemption History" subtitle="Redemptions completed at this branch" />
                <RangeSelector range={range} setRange={(v) => { setPage(1); setRange(v) }} />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-5">
                <KPI label="Total Redemptions" value={dateFiltered.length} color={C.blue} loading={loading} />
                <KPI label="Used" value={summary.completed ?? 0} color={C.green} loading={loading} />
                <KPI label="Claimed" value={summary.pending ?? 0} color={C.amber} loading={loading} />
                <KPI label="Cancelled / Expired" value={(summary.cancelled ?? 0) + (summary.expired ?? 0)} color={C.coral} loading={loading} />
            </div>

            <div className="flex flex-col gap-3 mb-4">
                <input
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                    placeholder="Search membership number or reward name…"
                    className="w-full rounded-xl border px-4 py-2 text-xs outline-none"
                    style={{ borderColor: C.border, color: C.text, background: C.card }}
                />
                <div className="flex flex-wrap gap-1.5 items-center">
                    <button onClick={() => { setStatusFilter(null); setPage(1) }}
                        className="px-3 py-1 rounded-full text-[11px] border transition-colors"
                        style={{
                            background: statusFilter === null ? C.amber : C.card,
                            borderColor: statusFilter === null ? C.amber : C.border,
                            color: statusFilter === null ? '#fff' : C.textSoft, cursor: 'pointer',
                        }}>All</button>
                    {statuses.map(s => (
                        <button key={s} onClick={() => { setStatusFilter(s); setPage(1) }}
                            className="px-3 py-1 rounded-full text-[11px] border transition-colors"
                            style={{
                                background: statusFilter === s ? statusColors[s] : C.card,
                                borderColor: statusFilter === s ? statusColors[s] : C.border,
                                color: statusFilter === s ? '#fff' : C.textSoft, cursor: 'pointer',
                            }}>{statusLabels[s]}</button>
                    ))}
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                    <thead>
                        <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                            {['Membership #', 'Reward', 'Points Used', 'Status', 'Redeemed At', 'Completed At'].map(h => (
                                <th key={h} className="text-left py-2 px-2 font-semibold uppercase tracking-wide" style={{ color: C.muted, fontSize: 10 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageData.map(r => (
                            <tr key={r.redemption_id} style={{ borderBottom: `1px solid ${C.border}` }}>
                                <td className="py-2.5 px-2">
                                    <span className="font-mono font-bold text-[11px]" style={{ color: C.amber }}>
                                        {membershipMap[r.loyalty_id] ?? '—'}
                                    </span>
                                </td>
                                <td className="py-2.5 px-2 font-semibold" style={{ color: C.text }}>{r.rewards?.reward_name ?? '—'}</td>
                                <td className="py-2.5 px-2 font-bold" style={{ color: C.blue }}>
                                    {r.points_used != null ? r.points_used.toLocaleString() : '—'}
                                </td>
                                <td className="py-2.5 px-2"><StatusBadge status={r.redemption_status} /></td>
                                <td className="py-2.5 px-2" style={{ color: C.textSoft }}>{fmtDate(r.redeemed_at)}</td>
                                <td className="py-2.5 px-2" style={{ color: C.textSoft }}>{fmtDate(r.completed_at)}</td>
                            </tr>
                        ))}
                        {pageData.length === 0 && (
                            <tr><td colSpan={6} className="py-8 text-center text-xs" style={{ color: C.muted }}>No redemptions match the current filters.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Pagination page={page} totalItems={filtered.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </Card>
    )
}

const parseAISections = (text) => {
    if (!text) return []
    const lines = text.split('\n')
    const sections = []
    let current = null
    for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed) continue
        if (trimmed.startsWith('### ')) {
            if (current) sections.push(current)
            current = { title: trimmed.replace(/^###\s*/, ''), bullets: [] }
        } else if (/^[-•*]\s+/.test(trimmed)) {
            if (!current) current = { title: null, bullets: [] }
            current.bullets.push(trimmed.replace(/^[-•*]\s+/, ''))
        } else {
            if (!current) current = { title: null, bullets: [] }
            current.bullets.push(trimmed)
        }
    }
    if (current) sections.push(current)
    return sections
}

const SECTION_COLORS = {
    'TIER DISTRIBUTION': { color: C.blue, icon: '🏆' },
    'REDEMPTION ACTIVITY': { color: C.purple, icon: '🎁' },
    'REWARD PERFORMANCE': { color: C.teal, icon: '📈' },
    'MEMBER GROWTH': { color: C.green, icon: '👥' },
    'AT-RISK / STALLED TIERS': { color: C.coral, icon: '⚠️' },
}
const DEFAULT_COLORS = [C.purple, C.teal, C.blue, C.green, C.coral, C.amber]

const renderBulletText = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g)
    return parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**')
            ? <strong key={i} style={{ fontWeight: 600, color: C.text }}>{part.slice(2, -2)}</strong>
            : part
    )
}

const MarkdownBlock = ({ text }) => {
    if (!text) return null
    const sections = parseAISections(text)
    if (!sections.length) return null
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {sections.map((section, idx) => {
                const key = section.title?.toUpperCase().trim()
                const cfg = (key && SECTION_COLORS[key]) || { color: DEFAULT_COLORS[idx % DEFAULT_COLORS.length], icon: '•' }
                return (
                    <div key={idx} style={{ borderRadius: 10, border: `1px solid ${cfg.color}28`, overflow: 'hidden' }}>
                        {section.title && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: cfg.color + '10', borderBottom: `1px solid ${cfg.color}20` }}>
                                <span style={{ fontSize: 13 }}>{cfg.icon}</span>
                                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: cfg.color }}>{section.title}</span>
                            </div>
                        )}
                        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                            {section.bullets.map((bullet, bIdx) => (
                                <div key={bIdx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.color, flexShrink: 0, marginTop: 7 }} />
                                    <span style={{ fontSize: 13, lineHeight: 1.65, color: C.text }}>{renderBulletText(bullet)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}



const AISidebar = ({
    open, onClose, summary, recommendations, loading, error, onGenerate,
    hasData, history, historyLoading, onDeleteHistory, branchName,
}) => {
    const [activeTab, setActiveTab] = useState("analysis")
    const [expandedId, setExpandedId] = useState(null)
    const [deletingId, setDeletingId] = useState(null)

    const handleDelete = async (id) => {
        setDeletingId(id)
        await onDeleteHistory(id)
        if (expandedId === id) setExpandedId(null)
        setDeletingId(null)
    }

    return (
        <>
            {open && <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.18)", zIndex: 999 }} />}
            <div style={{
                position: "fixed", top: 0, right: 0, bottom: 0,
                width: "min(420px, 100vw)",
                background: "#fff", borderLeft: `1px solid ${C.border}`, zIndex: 1000,
                display: "flex", flexDirection: "column",
                transform: open ? "translateX(0)" : "translateX(100%)",
                transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
                boxShadow: open ? "-4px 0 24px rgba(0,0,0,0.10)" : "none",
            }}>
                <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, background: C.purple + "06" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.purple, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24">
                                <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
                            </svg>
                        </div>
                        <div>
                            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>Branch Loyalty Analyst</p>
                            <p style={{ margin: 0, fontSize: 11, color: C.muted }}>{branchName ? branchName : "This branch"} · Tiers · Redemptions</p>
                        </div>
                    </div>
                    <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, borderRadius: 8, color: C.muted, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.dark }}>
                    {[{ id: "analysis", label: "Analysis" }, { id: "history", label: `History${history?.length ? ` (${history.length})` : ""}` }].map(tab => (
                        <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                            flex: 1, padding: "10px 0", background: "none", border: "none",
                            borderBottom: activeTab === tab.id ? `2px solid ${C.purple}` : "2px solid transparent",
                            color: activeTab === tab.id ? C.purple : C.muted,
                            fontSize: 12, fontWeight: activeTab === tab.id ? 700 : 400, cursor: "pointer", marginBottom: -1,
                        }}>{tab.label}</button>
                    ))}
                </div>

                <div style={{ flex: 1, overflowY: "auto", padding: "20px", minHeight: 0 }}>
                    {activeTab === "analysis" && (
                        <>
                            <button onClick={onGenerate} disabled={loading || !hasData} style={{
                                width: "100%", padding: "10px 16px", borderRadius: 50, border: "none",
                                background: loading || !hasData ? C.border : C.purple, color: "#fff",
                                fontSize: 12, fontWeight: 600, cursor: loading || !hasData ? "not-allowed" : "pointer",
                                display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                                marginBottom: summary ? 8 : 20,
                            }}>
                                {loading ? (
                                    <>
                                        <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 14, height: 14 }}>
                                            <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="4" />
                                            <path fill="#fff" d="M4 12a8 8 0 018-8v8z" />
                                        </svg>
                                        Analyzing branch data…
                                    </>
                                ) : summary ? (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                                            <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.62"/>
                                        </svg>
                                        Regenerate Analysis
                                    </>
                                ) : (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                                            <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
                                        </svg>
                                        Generate Analysis
                                    </>
                                )}
                            </button>

                            {summary && !loading && (
                                <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
                                    {["🤖 Gemini 2.5 Flash", "🏬 This branch only", "⚠️ For reference only"].map(label => (
                                        <span key={label} style={{ fontSize: 9, padding: "2px 8px", borderRadius: 50, border: `1px solid ${C.border}`, color: C.muted, background: C.dark }}>{label}</span>
                                    ))}
                                </div>
                            )}

                            <div style={{ padding: "10px 14px", borderRadius: 10, background: C.dark, border: `1px solid ${C.border}`, display: "flex", gap: 8, alignItems: "flex-start" }}>
                                <span style={{ fontSize: 13, flexShrink: 0 }}>⚠️</span>
                                <p style={{ margin: 0, fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
                                    <strong style={{ color: C.textSoft }}>AI-Generated Content.</strong>{" "}
                                    This analysis is produced by Google Gemini 2.5 Flash based on this branch's
                                    loyalty data only. Insights are pattern-based interpretations, not verified findings.
                                </p>
                            </div>

                            {error && (
                                <div style={{ background: C.coral + "15", border: `1px solid ${C.coral}44`, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: C.coral, marginTop: 16 }}>{error}</div>
                            )}

                            {!summary && !loading && !error && (
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: "40px 20px", textAlign: "center" }}>
                                    <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.purple + "12", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24">
                                            <path fill={C.purple} opacity="0.7" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
                                        </svg>
                                    </div>
                                    <div>
                                        <p style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 600, color: C.text }}>Branch Loyalty Interpretation</p>
                                        <p style={{ margin: 0, fontSize: 12, color: C.textSoft, maxWidth: 260, lineHeight: 1.6 }}>
                                            Click <strong>Generate Analysis</strong> for a plain-language read of tier health, redemptions, and reward performance at your branch.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {loading && (
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "40px 20px" }}>
                                    <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 28, height: 28 }}>
                                        <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
                                        <path fill={C.purple} d="M4 12a8 8 0 018-8v8z" />
                                    </svg>
                                    <p style={{ margin: 0, fontSize: 12, color: C.muted }}>Reading your branch's loyalty data…</p>
                                </div>
                            )}

                            {summary && !loading && (
                                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                                    <div style={{ background: C.purple + "08", border: `1px solid ${C.purple}28`, borderRadius: 12, overflow: "hidden" }}>
                                        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.purple}18`, background: C.purple + "10", display: "flex", alignItems: "center", gap: 8 }}>
                                            <span style={{ fontSize: 14 }}>🎁</span>
                                            <p style={{ margin: 0, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: C.purple }}>What the Data Shows</p>
                                        </div>
                                        <div style={{ padding: "12px 14px" }}><MarkdownBlock text={summary} /></div>
                                    </div>

                                    {recommendations && (
                                        <div style={{ background: C.teal + "08", border: `1px solid ${C.teal}28`, borderRadius: 12, overflow: "hidden" }}>
                                            <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.teal}18`, background: C.teal + "10", display: "flex", alignItems: "center", gap: 8 }}>
                                                <span style={{ fontSize: 14 }}>🔮</span>
                                                <p style={{ margin: 0, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: C.teal }}>Outlook & What to Anticipate</p>
                                            </div>
                                            <div style={{ padding: "12px 14px" }}><MarkdownBlock text={recommendations} /></div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {activeTab === "history" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {historyLoading ? (
                                <div className="flex items-center justify-center gap-2" style={{ height: 160 }}>
                                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
                                        <path fill={C.amber} d="M4 12a8 8 0 018-8v8z" />
                                    </svg>
                                </div>
                            ) : !history?.length ? (
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "40px 20px", textAlign: "center" }}>
                                    <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.muted + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🕓</div>
                                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text }}>No history yet</p>
                                    <p style={{ margin: 0, fontSize: 12, color: C.textSoft, lineHeight: 1.6 }}>Generated analyses for this branch will be saved here.</p>
                                </div>
                            ) : history.map(entry => {
                                const isOpen = expandedId === entry.id
                                const generatedDate = new Date(entry.generated_at)
                                return (
                                    <div key={entry.id} style={{ borderRadius: 12, border: `1px solid ${isOpen ? C.purple + "44" : C.border}`, overflow: "hidden" }}>
                                        <div onClick={() => setExpandedId(isOpen ? null : entry.id)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: isOpen ? C.purple + "08" : C.dark, cursor: "pointer", gap: 10 }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                                    <span style={{ fontSize: 10, fontWeight: 700, color: C.purple, background: C.purple + "15", borderRadius: 50, padding: "2px 8px" }}>{entry.scope_label || "Branch"}</span>
                                                    <span style={{ fontSize: 10, color: C.textSoft, background: C.border + "80", borderRadius: 50, padding: "2px 8px" }}>{entry.date_range}</span>
                                                </div>
                                                <p style={{ margin: "5px 0 0", fontSize: 11, color: C.muted }}>
                                                    {generatedDate.toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" })} at {generatedDate.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}
                                                </p>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                                <button onClick={(e) => { e.stopPropagation(); handleDelete(entry.id) }} disabled={deletingId === entry.id} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "3px 6px", cursor: deletingId === entry.id ? "not-allowed" : "pointer", color: C.muted, fontSize: 11, opacity: deletingId === entry.id ? 0.5 : 1 }}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                                                    </svg>
                                                </button>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isOpen ? C.purple : C.muted} strokeWidth="2" style={{ transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
                                                    <path d="M6 9l6 6 6-6"/>
                                                </svg>
                                            </div>
                                        </div>
                                        {isOpen && (
                                            <div style={{ padding: "12px 14px", borderTop: `1px solid ${C.purple}18`, display: "flex", flexDirection: "column", gap: 10 }}>
                                                <div style={{ background: C.purple + "06", borderRadius: 10, border: `1px solid ${C.purple}18`, overflow: "hidden" }}>
                                                    <div style={{ padding: "7px 12px", borderBottom: `1px solid ${C.purple}14`, background: C.purple + "0E", display: "flex", alignItems: "center", gap: 6 }}>
                                                        <span style={{ fontSize: 12 }}>🎁</span>
                                                        <span style={{ fontSize: 10, fontWeight: 700, color: C.purple, textTransform: "uppercase", letterSpacing: "0.07em" }}>What the Data Shows</span>
                                                    </div>
                                                    <div style={{ padding: "10px 12px" }}><MarkdownBlock text={entry.summary} /></div>
                                                </div>
                                                {entry.recommendations && (
                                                    <div style={{ background: C.teal + "06", borderRadius: 10, border: `1px solid ${C.teal}18`, overflow: "hidden" }}>
                                                        <div style={{ padding: "7px 12px", borderBottom: `1px solid ${C.teal}14`, background: C.teal + "0E", display: "flex", alignItems: "center", gap: 6 }}>
                                                            <span style={{ fontSize: 12 }}>🔮</span>
                                                            <span style={{ fontSize: 10, fontWeight: 700, color: C.teal, textTransform: "uppercase", letterSpacing: "0.07em" }}>Outlook & What to Anticipate</span>
                                                        </div>
                                                        <div style={{ padding: "10px 12px" }}><MarkdownBlock text={entry.recommendations} /></div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

const AIFloatingButton = ({ onClick, hasNew }) => (
    <button onClick={onClick} title="Open Branch Loyalty Analyst" style={{
        position: "fixed", bottom: 28, right: 28, width: 52, height: 52, borderRadius: "50%",
        background: C.purple, border: "none", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 4px 18px rgba(124,58,237,0.35)", zIndex: 998,
    }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
        </svg>
        {hasNew && <span style={{ position: "absolute", top: 2, right: 2, width: 10, height: 10, borderRadius: "50%", background: C.amber, border: "2px solid #fff" }} />}
    </button>
)

export default function BranchLoyaltyAnalytics({ branchId, branchName, canViewAnalytics }) {
    const { tiers, accounts, redemptions } = useBranchLoyaltyData(branchId)
    const loading = tiers === null || accounts === null || redemptions === null

    const [aiSummary, setAiSummary] = useState(null)
    const [aiRecommendations, setAiRecommendations] = useState(null)
    const [aiLoading, setAiLoading] = useState(false)
    const [aiError, setAiError] = useState(null)
    const [aiSidebarOpen, setAiSidebarOpen] = useState(false)
    const { history, loading: historyLoading, saveAnalysis, deleteAnalysis } = useBranchLoyaltyAIHistory(branchId)

    if (!branchId) {
        return (
            <Card>
                <p className="text-sm" style={{ color: C.muted }}>
                    Couldn't determine your assigned branch. Check that your staff profile has a branch linked.
                </p>
            </Card>
        )
    }

     if (!canViewAnalytics) {
        return (
            <Card>
                <div className="flex flex-col items-center gap-2 py-10 text-center">
                    <span style={{ fontSize: 28 }}>🔒</span>
                    <p className="m-0 text-sm font-semibold" style={{ color: C.text }}>
                        Analytics is restricted
                    </p>
                    <p className="m-0 text-xs" style={{ color: C.muted, maxWidth: 260 }}>
                        Your account doesn't have permission to view branch loyalty analytics. Ask an admin to update your access if you believe this is a mistake.
                    </p>
                </div>
            </Card>
        )
    }

    const handleGenerateInsights = async () => {
        setAiLoading(true)
        setAiError(null)
        setAiSummary(null)
        setAiRecommendations(null)

        try {
            const totalMembers = accounts?.length ?? 0
            const activeMembers = accounts?.filter(a => a.is_active).length ?? 0

            const tierCounts = {}
            for (const acc of accounts ?? []) {
                if (!acc.current_tier_id) continue
                tierCounts[acc.current_tier_id] = (tierCounts[acc.current_tier_id] || 0) + 1
            }
            const tierBreakdown = (tiers ?? []).map(t => ({
                name: t.tier_name, count: tierCounts[t.tier_id] || 0, minPoints: t.points_required,
            }))

            const totalRedemptions = redemptions?.length ?? 0
            const byStatus = (redemptions ?? []).reduce((acc, r) => {
                acc[r.redemption_status] = (acc[r.redemption_status] || 0) + 1
                return acc
            }, {})

            const rewardCounts = {}
            for (const r of redemptions ?? []) {
                const name = r.rewards?.reward_name ?? "Unknown"
                rewardCounts[name] = (rewardCounts[name] || 0) + 1
            }
            const topRewards = Object.entries(rewardCounts)
                .map(([name, count]) => ({ name, count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 5)

            const payload = {
                branchId,
                branchName,
                totalMembers,
                activeMembers,
                tierBreakdown,
                totalRedemptions,
                redemptionsByStatus: byStatus,
                topRewards,
            }

            const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
            const res = await fetch(`${BASE_URL}/api/summarize-loyalty`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ ...payload, mode: "interpret_and_forecast_branch" }),
            })
            if (!res.ok) throw new Error('Failed to generate insights')
            const { summary, recommendations } = await res.json()

            setAiSummary(summary)
            setAiRecommendations(recommendations)

            const { error } = await saveAnalysis({
                summary, recommendations, scopeLabel: branchName || "This Branch", dateRange: "All Time",
            })
            if (error) {
            console.error('Failed to save AI history:', error.message, error.details, error.hint)
                setAiError(prev => prev ?? `Analysis generated but not saved: ${error.message}`)
            }
        } catch (err) {
            setAiError(err.message || 'Failed to generate analysis. Please try again.')
        } finally {
            setAiLoading(false)
        }
    }

    if (!branchId) {
        return (
            <Card>
                <p className="text-sm" style={{ color: C.muted }}>
                    Couldn't determine your assigned branch. Check that your staff profile has a branch linked.
                </p>
            </Card>
        )
    }

    return (
        <div className="flex flex-col gap-5">
            <BranchTierPanel tiers={tiers} accounts={accounts} loading={loading} />
            <BranchRewardsChart redemptions={redemptions} loading={loading} />
            <BranchRedemptionTable redemptions={redemptions} accounts={accounts} loading={loading} />

            <AISidebar
                open={aiSidebarOpen}
                onClose={() => setAiSidebarOpen(false)}
                summary={aiSummary}
                recommendations={aiRecommendations}
                loading={aiLoading}
                error={aiError}
                onGenerate={handleGenerateInsights}
                hasData={!loading}
                history={history}
                historyLoading={historyLoading}
                onDeleteHistory={deleteAnalysis}
                branchName={branchName}
            />
            <AIFloatingButton onClick={() => setAiSidebarOpen(true)} hasNew={!!aiSummary} />
        </div>
    )
}